import { Controller } from "@hotwired/stimulus";

// Hierarchical list filter controller
// Supports parent groups with child items - shows parent if any child matches
export default class extends Controller {
  static targets = ['query', 'group', 'groupHeader', 'item', 'empty'];
  // Opt out of the scroll-to-checked on connect where checked items are the norm and the list isn't
  // in its own scroll container (e.g. a summary table above the fold, which would otherwise scroll the
  // window past the content on load). Defaults on for the pinned pickers.
  static values = { scrollToChecked: { type: Boolean, default: true } };

  connect() {
    // scroll to checked if its set.
    if (!this.scrollToCheckedValue) return;
    const checked = this.element.querySelector('input:checked');
    if (checked) {
      requestAnimationFrame(() => {
        checked.closest('[data-list-filter-target="item"]')?.scrollIntoView({ block: 'center' });
      });
    }
  }

  // A re-render of the list replaces every row, dropping the inline display:none this controller set
  // while the query box keeps its text. Re-filter once the new rows land. Coalesced through a single
  // animation frame because this fires per row — an account can carry 2,000+ of them.
  itemTargetConnected() {
    if (!this.hasQueryTarget || this.queryTarget.value === '' || this.refilterPending) return;

    this.refilterPending = true;
    requestAnimationFrame(() => {
      this.refilterPending = false;
      if (this.element.isConnected) this.filter();
    });
  }

  filter() {
    const query = this.queryTarget.value.toLowerCase().trim();
    let visibleCount = 0;

    // Handle groups (managers with sub-accounts)
    this.groupTargets.forEach((group, index) => {
      const header = this.groupHeaderTargets[index];
      const childItems = group.querySelectorAll('[data-list-filter-target="item"]');

      const headerText = header ? header.textContent.toLowerCase() : '';
      const headerMatches = query === '' || headerText.includes(query);

      // Check each child item
      let hasVisibleChild = false;
      childItems.forEach(child => {
        const childText = child.textContent.toLowerCase();
        const childMatches = query === '' || childText.includes(query);

        // Show child if it matches OR if the parent header matches (show all children)
        const showChild = childMatches || headerMatches;
        child.style.display = showChild ? '' : 'none';

        // Highlight matching children to show which items actually match the query
        if (showChild) {
          if (childMatches) {
            child.classList.remove('opacity-50');
          } else if (headerMatches) {
            // Dim non-matching children when parent matches to show context
            child.classList.add('opacity-50');
          }
          hasVisibleChild = true;
        }
      });

      // Show group if header matches OR any child matches
      const showGroup = headerMatches || hasVisibleChild;
      group.style.display = showGroup ? '' : 'none';

      if (showGroup) visibleCount++;
    });

    // Handle standalone items (not in a group)
    this.itemTargets.forEach(item => {
      // Skip items that are inside a group (already handled above)
      if (item.closest('[data-list-filter-target="group"]')) return;

      // ADDS to the row's own text rather than replacing it: data-filter-text carries what textContent
      // can't see (a group's member names live in a <template>, a detached fragment), but the visible
      // text is still matchable — replacing it silently stopped group rows matching their "Group"
      // pill and their "N conversion actions" count.
      const extra = item.dataset.filterText;
      const text = (extra ? `${extra} ${item.textContent}` : item.textContent).toLowerCase();
      const matches = query === '' || text.includes(query);
      item.style.display = matches ? '' : 'none';
      if (matches) visibleCount++;
    });

    // Show/hide empty state
    if (this.hasEmptyTarget) {
      this.emptyTarget.style.display = visibleCount > 0 ? 'none' : '';
    }
  }

  // A filter box has nothing to submit. Without this, Enter fires the form's default submit — which,
  // wherever this filter sits inside a form, is whichever submit button happens to come first in the
  // DOM. In the column picker that's "Reset to defaults", so a stray Enter silently discarded the
  // viewer's pinned conversions.
  preventSubmit(event) {
    event.preventDefault();
  }

  clear() {
    this.queryTarget.value = '';
    this.filter();
  }
}

